// feature types

import { RolodexEntryJson } from 'types/rolodex/rolodexEntries'
import { parseNumber, parseString } from 'utils/parsingHelpers'

import { GeckoEntryBase } from './geckoEntry'

export type SiteModelCoordinate = {
  lat: number
  lon: number
  height: number
}

// ----------------------------------------------------------------------------
// Site Feature

export interface SiteFeatureBase {
  id: string
  unit_id: string | null
  area_id: string | null
}

export type SiteFeature = SiteFeaturePrimitive | SiteFeatureComposite

// ----------------------------------------------------------------------------
// Site Feature Primitive

export enum SiteFeaturePrimitiveKind {
  Box = 'box',
  Cylinder = 'cylinder',
  Sphere = 'sphere',
}

interface SiteFeaturePrimitiveBase extends SiteFeatureBase {
  coordinate: SiteModelCoordinate
  rotation: [number, number, number]
}

export interface SiteFeaturePrimitiveBox extends SiteFeaturePrimitiveBase {
  kind: SiteFeaturePrimitiveKind.Box
  geometry: {
    width: number
    height: number
    depth: number
  }
}

export interface SiteFeaturePrimitiveCylinder extends SiteFeaturePrimitiveBase {
  kind: SiteFeaturePrimitiveKind.Cylinder
  geometry: {
    bottom_radius: number
    top_radius: number
    height: number
    theta_start: number | null
    theta_end: number | null
  }
}

export interface SiteFeaturePrimitiveSphere extends SiteFeaturePrimitiveBase {
  kind: SiteFeaturePrimitiveKind.Sphere
  geometry: {
    radius: number
    theta_start: number | undefined
    theta_end: number | undefined
    phi_start: number | undefined
    phi_end: number | undefined
  }
}

export type SiteFeaturePrimitive =
  | SiteFeaturePrimitiveBox
  | SiteFeaturePrimitiveCylinder
  | SiteFeaturePrimitiveSphere

export function isSitePrimitiveKind(
  value: string
): value is SiteFeaturePrimitiveKind {
  return Object.values<string>({ ...SiteFeaturePrimitiveKind }).includes(value)
}

export function isSiteFeaturePrimitive(
  feature: SiteFeatureBase
): feature is SiteFeaturePrimitive {
  if (!('kind' in feature) || typeof feature.kind !== 'string') {
    return false
  }

  return isSitePrimitiveKind(feature.kind)
}

// ----------------------------------------------------------------------------
// Site Feature Composites

export enum SiteFeatureCompositeKind {
  Pin = 'pin',
  Polygon = 'polygon',
  Label = 'label',
}

export interface SiteFeaturePin extends SiteFeaturePrimitiveBase {
  kind: SiteFeatureCompositeKind.Pin
  name: string
  color?: string
  geometry: {
    radius: number
  }
}

export interface SiteFeaturePolygon extends SiteFeatureBase {
  kind: SiteFeatureCompositeKind.Polygon
  name: string
  coordinates: SiteModelCoordinate[]
}

export interface SiteFeatureLabel extends SiteFeatureBase {
  kind: SiteFeatureCompositeKind.Label
  text: string
  coordinate: SiteModelCoordinate
}

export type SiteFeatureComposite =
  | SiteFeaturePin
  | SiteFeaturePolygon
  | SiteFeatureLabel

// ----------------------------------------------------------------------------
// Site Model

/**
 * An entry containing a site model with various features like polygons, labels, and primitives
 */
export class GeckoSiteModel extends GeckoEntryBase<
  {
    effective_date: number
    features: Record<string, SiteFeature>
    camera_origin: {
      position: [number, number, number] | undefined
      rotation: [number, number, number] | undefined
    }
    unit_id: string | null
    area_id: string | null
  },
  {
    site: string
    organization: string
  }
> {
  //- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  /**
   * A simple constructor, private so that a more descriptive factory method
   * (which can return undefined) is used instead
   * @param json The raw JSON to create an instance from
   * @private
   */
  private constructor(json: RolodexEntryJson) {
    const cameraOrigin = json.data.camera_origin as
      | {
          position?: [number, number, number]
          rotation?: [number, number, number]
        }
      | undefined

    super(
      json,
      {
        effective_date: parseNumber(json.data.effective_date) ?? 0,
        features: json.data.features as Record<string, SiteFeature>,
        camera_origin: {
          position: cameraOrigin?.position,
          rotation: cameraOrigin?.rotation,
        },
        unit_id: (!json.data.unit_id
          ? null
          : parseString(json.data.unit_id)) as string | null,
        area_id: (!json.data.area_id
          ? null
          : parseString(json.data.area_id)) as string | null,
      },
      {
        site: parseString(json.tags.site) ?? '',
        organization: parseString(json.tags.organization) ?? '',
      },
      {}
    )
  }

  //- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  /**
   * A simple factory method which creates a site model from raw JSON
   * @param json The raw JSON to create an instance from
   * @constructor
   */
  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoSiteModel(json)
  }

  /**
   * A simple comparison method, used to sort entries by their effective dates
   * @param a     The first entry
   * @param b     The second entry
   */
  public static DefaultSort(a: GeckoSiteModel, b: GeckoSiteModel) {
    return b.data.effective_date - a.data.effective_date
  }
}
